// filepath: /src/components/Auth/LoginModal.js
import React, { useState, useEffect, useRef } from 'react';
import './LoginModal.css';
import urls from '../../config/urls';

const LoginModal = ({ isOpen, onClose, onLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar errores al escribir
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleForgotEmailChange = (e) => {
    setForgotEmail(e.target.value);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'El usuario es requerido';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    
    try {
      const response = await fetch(urls.apiEndpoints.login, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json;charset=UTF-8',
          'Authorization': 'undefined',
          'makeSecure': 'false',
          'think': 'd0xGQB1vaLyT788o6d6/vQxkCA3TVnoF6toLwDz8BFyjHy6DB87pdeJh9rZzJ5WFAwXg9phsSwudGqIhaD2sFYZIxDbdIWSK3GfDAvrtT10=',
        },
        body: JSON.stringify({
          vc_email: formData.username,
          vc_password: formData.password,
          vc_uuid: 'web',
          vc_descripcion: 'mac'
        })
      });

      const data = await response.json();

      if (response.ok && data.data) {
        const { usuario, instituciones, accessToken } = data.data;
        
        // Guardar datos en localStorage según la estructura requerida
        localStorage.setItem('__amplify__institutions', JSON.stringify(instituciones));
        localStorage.setItem('__amplify__token', accessToken);
        localStorage.setItem('__amplify__user', JSON.stringify(usuario));
        localStorage.setItem('__amplify__nombre', usuario.vc_nombre);
        
        // Si hay instituciones, guardar la primera como institución activa
        if (instituciones && instituciones.length > 0) {
          localStorage.setItem('__amplify__institution', JSON.stringify(instituciones[0]));
        }

        // Llamar al callback de login con la información del usuario
        onLogin({
          username: usuario.vc_email,
          name: `${usuario.vc_nombre} ${usuario.vc_apellidoPaterno || ''} ${usuario.vc_apellidoMaterno || ''}`.trim(),
          avatar: null,
          role: instituciones?.[0]?.roles?.[0]?.vc_slug || 'user',
          id: usuario.id,
          email: usuario.vc_email
        });

        // Guardar credenciales para login automático en el sistema
        localStorage.setItem('thinkhealth_last_login', JSON.stringify({
          username: formData.username,
          password: formData.password,
          timestamp: Date.now()
        }));

        // Cerrar modal y limpiar formulario
        onClose();
        setFormData({ username: '', password: '' });
        
      } else {
        setErrors({ 
          general: data.message || 'Credenciales inválidas. Por favor, verifica tu usuario y contraseña.' 
        });
      }
    } catch (error) {
      console.error('Error en login:', error);
      setErrors({ 
        general: 'Error de conexión. Por favor, verifica que el servidor esté disponible e intenta nuevamente.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotEmail.trim()) {
      setErrors({ forgotEmail: 'El email es requerido' });
      return;
    }

    setIsLoading(true);
    
    try {
      // Simular envío de email de recuperación
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Se ha enviado un enlace de recuperación a tu email');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      setErrors({ forgotEmail: 'Error al enviar email de recuperación' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="login-modal-backdrop" onClick={handleBackdropClick}>
      <div className="login-modal">
        <div className="login-modal-header">
          <h2 className="login-title">
            {showForgotPassword ? 'Recuperar Contraseña' : 'Iniciar Sesión'}
          </h2>
          <button 
            className="login-close-button"
            onClick={onClose}
            aria-label="Cerrar modal"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="login-modal-body">
          {!showForgotPassword ? (
            // Formulario de Login
            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="username" className="form-label">
                  Usuario o Email
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className={`glass-input ${errors.username ? 'error' : ''}`}
                  placeholder="Ingresa tu usuario o email"
                  disabled={isLoading}
                />
                {errors.username && (
                  <span className="error-message">{errors.username}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Contraseña
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`glass-input ${errors.password ? 'error' : ''}`}
                  placeholder="Ingresa tu contraseña"
                  disabled={isLoading}
                />
                {errors.password && (
                  <span className="error-message">{errors.password}</span>
                )}
              </div>

              {errors.general && (
                <div className="error-message general-error">{errors.general}</div>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className={`glass-button primary large ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Iniciando sesión...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10 1v18M1 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Iniciar Sesión
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => setShowForgotPassword(true)}
                  disabled={isLoading}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            </form>
          ) : (
            // Formulario de Recuperar Contraseña
            <form onSubmit={handleForgotPassword} className="login-form">
              <p className="forgot-password-description">
                Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
              </p>

              <div className="form-group">
                <label htmlFor="forgotEmail" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="forgotEmail"
                  value={forgotEmail}
                  onChange={handleForgotEmailChange}
                  className={`glass-input ${errors.forgotEmail ? 'error' : ''}`}
                  placeholder="Ingresa tu email"
                  disabled={isLoading}
                />
                {errors.forgotEmail && (
                  <span className="error-message">{errors.forgotEmail}</span>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className={`glass-button primary large ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Enviar Enlace
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => setShowForgotPassword(false)}
                  disabled={isLoading}
                >
                  Volver al inicio de sesión
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
